import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { QueryEntity } from '@datorama/akita';
import { OrgScopeStorageService } from '@y42/org';
import { ACTIVE_USER_ID_API_URL_PARAM, ORG_SPACE_API_URL_PARAM } from '@y42/model';
import { Y42EntityStore } from '@y42/state';
import { takePromise } from '@y42/util/take-promise';
import { AbstractFeatureEntityState, UserRelatedProperty, UserRelatedState } from '../models';
import { AbstractFeatureEntityStore } from '../state';

export abstract class AbstractUserRelatedDataService<
  State extends UserRelatedState = AbstractFeatureEntityState & UserRelatedState,
> {
  constructor(
    private readonly config: {
      http?: HttpClient;
      store: State extends AbstractFeatureEntityState ? AbstractFeatureEntityStore<State> : Y42EntityStore<State>;
      query: QueryEntity<State>;
      URLs: Record<UserRelatedProperty, string>;
    },
  ) {}

  private readonly storageService = inject(OrgScopeStorageService);

  async getLastUsed() {
    const items = await this.getItems<{ id: string; time: number }>('last_used');
    const last_used = (items || []).reduce(
      (acc, curr) => ({ ...acc, [curr.id]: curr.time }),
      {} as UserRelatedState['last_used'],
    );

    this.config.store.update({ last_used } as State);
  }

  async getStarred() {
    const items = await this.getItems<string>('starred');
    const starred = (items || []).reduce((acc, id) => ({ ...acc, [id]: true }), {} as UserRelatedState['starred']);
    this.config.store.update({ starred } as State);
  }

  async setLastUsed(entityId: string) {
    const currentTime = Math.round(new Date().getTime());
    const lastUsed = { ...this.config.query.getValue().last_used };
    lastUsed[entityId] = currentTime;

    const response = await this.set(
      'last_used',
      Object.entries(lastUsed).map(([id, time]) => ({ id, time })),
    );

    this.config.store.update({
      last_used: response.reduce((acc, curr) => ({ ...acc, [curr.id]: curr.time }), {}),
    } as State);
  }

  async toggleStarred(entityId: string) {
    const starred = { ...this.config.query.getValue().starred };

    if (starred[entityId]) {
      delete starred[entityId];
    } else {
      starred[entityId] = true;
    }

    const response = await this.set('starred', Object.keys(starred));
    this.config.store.update({ starred: response.reduce((acc, curr) => ({ ...acc, [curr]: true }), {}) } as State);
  }

  private async getItems<Return>(property: UserRelatedProperty) {
    if (this.config.store instanceof AbstractFeatureEntityStore) {
      this.config.store.incrementLoading();
    }
    const key = this.getKey(property);
    const items = await takePromise(this.storageService.load<Return[]>(key));
    if (this.config.store instanceof AbstractFeatureEntityStore) {
      this.config.store.decrementLoading();
    }
    return items;
  }

  private async set<P extends UserRelatedProperty, Body = unknown[]>(property: P, body: Body) {
    const key = this.getKey(property);
    return await takePromise(this.storageService.save(key, body));
  }

  private getKey(property: UserRelatedProperty) {
    const keys = [ACTIVE_USER_ID_API_URL_PARAM, ORG_SPACE_API_URL_PARAM, this.config.URLs[property]];
    const storageKey = keys.join('_');
    return storageKey;
  }
}
